// STEP OMS-OUTBOUND-UNIFY-3 (2026-02) · ⑤ 송장등록 stage 를 ④ 출고관리로 흡수.
// - 기존 ⑤ 송장등록 (invoice · awaiting_tracking+inspected) 은 별도 stage 였으나,
//   FulfillmentSection 내부 [송장대기] sub-tab 과 동일 주문셋을 두 곳에서
//   표기하여 2중노출 · 사용자 혼동 유발.  본 STEP 에서 `invoice` STAGE 를
//   제거하고 ④ 출고관리의 ws 를 ['shippable','picking','inspected','awaiting_tracking']
//   로 확장 · ⑤ 발송 / ⑥ 완료 로 renumber.
// - 송장 엑셀 일괄등록 · 선택 발송처리 · 출고 엑셀 다운로드 등 기존 ⑤ 기능은
//   FulfillmentSection 의 [송장대기] sub-tab 으로 흡수 (기능/API 삭제 없음).
// - Backward compat: `?stage=invoice` deep-link 는 SimpleOrders 에서 자동으로
//   `?stage=fulfillment` 로 redirect.
//
// §14 LOCK (updated): OMS 6단계 · ① 주문확인 · ② 결제확인 · ③ 재고확인
//                     · ④ 출고관리 · ⑤ 발송 · ⑥ 완료.
//
// §13 SOURCE OF TRUTH — canonical stage 는 아래 두 source 만 사용:
//   1. `/seller/oms/summary` counts (WORK_STATUS 기반)
//   2. Legacy `/seller/orders?status=<X>` (payment_pending · shipping · delivered)
//
// §15 LOCK (updated): ⑤ 발송 = order.status='shipping' (변경 없음).
// §11: 홈 count = 각 stage 에 현재 머물러 있는 ACTIVE 주문 수.

export const STAGES = [
  { key: 'order_confirm',   label: '① 주문 확인',    short: '주문확인',
      mode: 'legacy', legacyStatus: 'payment_pending',
      desc: '결제 대기 · 결제 확인 필요' },
  { key: 'payment_confirm', label: '② 결제 확인',    short: '결제확인',
      mode: 'oms', ws: 'new_paid',
      desc: '결제 완료 · 처리 시작 대기' },
  { key: 'stock_check',     label: '③ 재고 확인',    short: '재고확인',
      mode: 'oms',
      ws: ['waiting_stock', 'partial_shippable', 'has_out_of_stock'],
      filterKey: 'waiting_stock',
      desc: '재고 부족 · 부분 출고 가능' },
  // STEP OMS-OUTBOUND-UNIFY-3 · ④ 출고 관리 (구 ④ 상품준비 + ⑤ 송장등록 통합).
  //   FulfillmentSection embed 에서 [출고준비 = pending+picking] [송장대기 = inspected]
  //   [출고완료 = shipped] 3-tab 으로 내부 표시.
  { key: 'fulfillment',     label: '④ 출고 관리',    short: '출고관리',
      mode: 'oms', ws: ['shippable', 'picking', 'inspected', 'awaiting_tracking'],
      filterKey: 'shippable',
      desc: '피킹 · 검수 · 송장 등록' },
  // §15 · ⑤ 발송 = order.status='shipping' (배송추적 없음)
  { key: 'ship',            label: '⑤ 발송',         short: '발송',
      mode: 'legacy', legacyStatus: 'shipping',
      desc: '판매자 발송처리 완료' },
  { key: 'done',            label: '⑥ 완료',         short: '완료',
      mode: 'oms', ws: 'shipped_done',
      desc: '출고 완료 · 배송 완료' },
];

// STEP OMS-OUTBOUND-UNIFY-3 · Backward-compat map for legacy deep-links.
export const LEGACY_STAGE_ALIAS = {
  invoice:  'fulfillment',   // ⑤ 송장등록 → ④ 출고관리 (송장대기 sub-tab)
  prepare:  'fulfillment',   // 구 ④ 상품준비 → ④ 출고관리
};

// Legacy `/seller/orders` endpoint 는 total 필드가 없다 · `.length` 로 카운트.
// 200 은 REAL 판매자 트래픽에서 잘려 mismatch 를 유발 → 1000 으로 상향.
// 이번 STEP §22 절대 금지: 새 API/DB migration 없음 → limit 상향이 유일한
// 최소 수정.  향후 legacy total 필드 추가 시 대체 가능.
export const LEGACY_COUNT_LIMIT = 1000;

/** counts shape:
 *  {
 *    oms:         object    // /seller/oms/summary → counts
 *    pay_pending: number    // /seller/orders?status=payment_pending  .length
 *    shipping:    number    // /seller/orders?status=shipping         .length
 *  }
 *  stage: STAGES 원소.  Return the ACTIVE count for that stage (§11). */
export const stageCount = (counts, stage) => {
  if (!counts || !stage) return 0;
  if (stage.mode === 'legacy') {
    if (stage.legacyStatus === 'payment_pending') return counts.pay_pending || 0;
    if (stage.legacyStatus === 'shipping')        return counts.shipping    || 0;
    return 0;
  }
  const oms = counts.oms || {};
  if (Array.isArray(stage.ws)) {
    return stage.ws.reduce((a, w) => a + (oms[w] || 0), 0);
  }
  return oms[stage.ws] || 0;
};

// Convenience: fetch canonical counts snapshot (single-flight for both Home
// and Orders).  Uses only existing endpoints — no new APIs.
export const fetchOmsCounts = async (api) => {
  const [rOms, rPay, rShip] = await Promise.all([
    api.get('/seller/oms/summary'),
    api.get('/seller/orders',
       { params: { status: 'payment_pending', limit: LEGACY_COUNT_LIMIT } })
       .catch(() => ({ data: [] })),
    api.get('/seller/orders',
       { params: { status: 'shipping',        limit: LEGACY_COUNT_LIMIT } })
       .catch(() => ({ data: [] })),
  ]);
  const payArr  = Array.isArray(rPay.data)  ? rPay.data  : (rPay.data?.items  || []);
  const shipArr = Array.isArray(rShip.data) ? rShip.data : (rShip.data?.items || []);
  return {
    oms:         rOms.data?.counts || {},
    pay_pending: payArr.length,
    shipping:    shipArr.length,
    _total:      rOms.data?.total || 0,
    _label:      rOms.data?.label || {},
  };
};
